import React, { useRef, useEffect } from 'react'
import { WebGLRenderer } from '../webgl/WebGLRenderer'

interface WebGLCanvasProps {
  verticalViewingAngle: number
  currentTime: Date
}

const WebGLCanvas: React.FC<WebGLCanvasProps> = ({ verticalViewingAngle, currentTime }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const rendererRef = useRef<WebGLRenderer | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Initialize WebGL renderer
    const renderer = new WebGLRenderer(canvasRef.current)
    rendererRef.current = renderer

    // Set initial viewing range
    renderer.updateViewingRange(verticalViewingAngle)

    // Set initial time
    renderer.updateTime(currentTime)

    // Start animation loop
    let animationFrameId: number
    const animate = () => {
      renderer.render()
      animationFrameId = requestAnimationFrame(animate)
    }
    animate()

    // Handle window resize
    const handleResize = () => {
      if (containerRef.current) {
        const dpr = window.devicePixelRatio || 1
        const width = containerRef.current.clientWidth * dpr
        const height = containerRef.current.clientHeight * dpr
        renderer.resize(width, height)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize() // Initial size

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationFrameId)
      renderer.dispose()
    }
  }, [])

  // Update viewing range when prop changes
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.updateViewingRange(verticalViewingAngle)
    }
  }, [verticalViewingAngle])

  // Update time when prop changes
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.updateTime(currentTime)
    }
  }, [currentTime])

  return (
    <div ref={containerRef} className="flex-1 w-full overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full bg-black"
        style={{ display: 'block' }}
      />
    </div>
  )
}

export default WebGLCanvas
